/*******************************************************************************
  timeUtils.js

  Author: Advait;
*******************************************************************************/

/**
 * Check if date range is within 31 days.
 * @param {string} startDate - Date string in YYYY-MM-DD format
 * @param {string} endDate - Date string in YYYY-MM-DD format
 * @param {number} maxDays - Maximum allowed number of days between startDate and endDate
 * @returns {boolean} True if endDate - startDate <= maxDays
 */
function isWithinDays(startDate, endDate, maxDays) {
  if (!startDate || !endDate) return false;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const msPerDay = 24 * 60 * 60 * 1000;
  const diffDays = (end - start) / msPerDay;
  return diffDays <= maxDays;
}

/**
 * Validate date format using regex (YYYY-MM-DD)
 * @param {string} date - Date string to validate
 * @returns {boolean} True if date is in valid format
 */
function isValidDate(date) {
  const regex = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD
  return regex.test(date);
}

/**
 * Compare start and end dates to ensure start date ≤ end date
 * @param {string} startDate - Date string in YYYY-MM-DD format
 * @param {string} endDate - Date string in YYYY-MM-DD format
 * @returns {boolean} True if startDate is before or equal to endDate
 */
function isStartDateBeforeEndDate(startDate, endDate) {
  return new Date(startDate) <= new Date(endDate);
}

/*
 * Validate start and end dates.
 * Optionally check if the date range is within a specified number of days.
 * @param {string} startDate - Date string in YYYY-MM-DD format
 * @param {string} endDate - Date string in YYYY-MM-DD format
 * @param {boolean} checkDaysRange - Whether to check if the date range is within maxDays
 * @param {number} maxDays - Maximum allowed number of days between startDate and endDate
 * @returns {boolean} True if dates are valid (and within range if specified)
 */

export function validateDates(startDate, endDate, checkDaysRange = false, maxDays = 31) {
  // Check if both dates are valid
  const isStartDateValid = isValidDate(startDate);
  const isEndDateValid = isValidDate(endDate);
  const isStartBeforeEnd = isStartDateBeforeEndDate(startDate, endDate);
  const isWithinDaysRange = checkDaysRange ? isWithinDays(startDate, endDate, maxDays) : true;

  return isStartDateValid && isEndDateValid && isStartBeforeEnd && isWithinDaysRange;
}
